import React, { useState, useEffect, useCallback, useContext, useRef, ReactNode } from 'react';
import { parseClaimsFromHeader, isSessionValid } from '../../../utils/auth/ClaimsParser';
import sessionTimeoutService from '../../../event_handlers/SessionTimeoutService';
import { refreshAuthToken } from '../../../services/Users';
import { CsrfContext } from '../../../providers/CsrfProvider';
import { performLogout } from '../../../utils/auth/LogoutHandler';
import { useNavigate } from 'react-router-dom';

export interface AuthClaims {
    user_sub?: string;
    /** User's email; captured at login and required for session refresh (Stay Logged In) */
    email?: string;
    [key: string]: any;
}

export interface AuthContextType {
    authClaims: AuthClaims | null;
    setAuthClaims: (claims: AuthClaims | null) => void;
    showTimeoutModal: boolean;
    onStayLoggedIn: () => Promise<void>;
    onLogout: () => Promise<void>;
}

export const AuthContext = React.createContext<AuthContextType | null>(null);

interface AuthProviderProps {
    children: ReactNode;
}

// Create a provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    // Don't block on csrfToken - it's fetched asynchronously and not needed for initial render
    const csrfContext = useContext(CsrfContext) as unknown as { csrfToken: string | null; setCsrfToken: (token: string) => void };
    const csrfToken = csrfContext?.csrfToken || null;
    const navigate = useNavigate();

    const [authClaims, setAuthClaims] = useState<AuthClaims | null>(() => {
        // Retrieve saved authClaims from local storage
        try {
            const storedClaims = localStorage.getItem('authClaims');
            const parsedClaims = storedClaims && storedClaims !== 'undefined' ? JSON.parse(storedClaims) : null;
            
            // Validate that session cookies are still valid
            // If we have claims but session is invalid, clear them
            if (parsedClaims && !isSessionValid()) {
                localStorage.removeItem('authClaims');
                return null;
            }
            
            return parsedClaims;
        } catch (error) {
            console.error('Error parsing stored auth claims:', error);
            return null;
        }
    });

    const [showTimeoutModal, setShowTimeoutModal] = useState<boolean>(false);
    const lastRefreshAtRef = useRef<number>(0);

    // Handle session expiration
    const handleSessionExpired = useCallback(async () => {
        setShowTimeoutModal(false);
        await performLogout(csrfToken, setAuthClaims, (path: string, state?: object) => {
            navigate(path, { state });
        }, () => {
            // Stop session monitoring after logout
            sessionTimeoutService.onLogout();
        }, { logoutReason: 'session_expired' });
    }, [csrfToken, navigate]);

    // Handle session timeout warning - show modal to allow user to stay logged in
    const handleSessionWarning = useCallback(() => {
        setShowTimeoutModal(true);
    }, []);

    // Handle user choosing to stay logged in - refresh token and restart monitoring
    const handleStayLoggedIn = useCallback(async () => {
        try {
            if (!csrfToken) {
                handleSessionExpired();
                return;
            }

            const email = authClaims?.email;
            if (!email || typeof email !== 'string' || !String(email).trim()) {
                handleSessionExpired();
                return;
            }

            // Refresh the auth token
            const response = await refreshAuthToken(csrfToken, email);

            // Parse claims from the response header (backend may not include email in refresh response)
            const newClaims = parseClaimsFromHeader(response);
            const claimsToSet = newClaims
                ? { ...newClaims, email: newClaims.email ?? authClaims?.email }
                : authClaims;

            if (newClaims) {
                setAuthClaims(claimsToSet);
                setShowTimeoutModal(false);
                lastRefreshAtRef.current = Date.now();
                sessionTimeoutService.refreshSession();
            } else {
                setShowTimeoutModal(false);
                lastRefreshAtRef.current = Date.now();
                sessionTimeoutService.refreshSession();
            }
        } catch (error) {
            handleSessionExpired();
        }
    }, [csrfToken, authClaims, handleSessionExpired]);

    // Handle user choosing to log out (from session timeout modal)
    const handleLogout = useCallback(async () => {
        setShowTimeoutModal(false);
        await performLogout(csrfToken, setAuthClaims, (path: string, state?: object) => {
            navigate(path, { state });
        }, () => {
            // Stop session monitoring after logout
            sessionTimeoutService.onLogout();
        }, { logoutReason: 'session_expired' });
    }, [csrfToken, navigate]);

    // Start session monitoring when user is logged in
    useEffect(() => {
        if (authClaims) {
            // Add a small delay to ensure cookies are set after login response
            const timeoutId = setTimeout(() => {
                sessionTimeoutService.startMonitoring(handleSessionWarning, handleSessionExpired);
            }, 100);

            return () => {
                clearTimeout(timeoutId);
                sessionTimeoutService.stopMonitoring();
            };
        } else {
            sessionTimeoutService.stopMonitoring();
        }
    }, [authClaims, handleSessionWarning, handleSessionExpired]);

    // Periodically validate session cookies to catch expired sessions
    // This ensures that if cookies expire or are cleared externally, we detect it
    useEffect(() => {
        if (!authClaims) {
            return;
        }

        // Check session validity every 30 seconds; skip for 45s after Stay Logged In so new cookies are visible
        const validationInterval = setInterval(() => {
            if (Date.now() - lastRefreshAtRef.current < 45 * 1000) return;
            if (!isSessionValid()) {
                setAuthClaims(null);
                handleSessionExpired();
            }
        }, 30000); // Check every 30 seconds

        return () => {
            clearInterval(validationInterval);
        };
    }, [authClaims, handleSessionExpired]);

    // Save authClaims to local storage
    useEffect(() => {
        if (authClaims) {
            localStorage.setItem('authClaims', JSON.stringify(authClaims));
        } else {
            localStorage.removeItem('authClaims');
        }
    }, [authClaims]);

    const authContext: AuthContextType = {
        authClaims: authClaims,
        setAuthClaims: setAuthClaims,
        showTimeoutModal,
        onStayLoggedIn: handleStayLoggedIn,
        onLogout: handleLogout
    };

    return (
        <AuthContext.Provider value={authContext}>
            {children}
        </AuthContext.Provider>
    );
};
